import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { classNames } from "primereact/utils";
import localeText from "../../../../locale";
import { formatDate } from "../../../../utilities/formatDate";
import { buildSearchText, normalizeText } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import SearchInput from "../../components/list/SearchInput";
import StatusBadge from "../../components/list/StatusBadge";
import EmptyState from "../../components/list/EmptyState";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import useAdminUsers from "./hooks/useAdminUsers";
import UserActions from "./components/UserActions";
import { getUserDocuments } from "./userDocuments";
import "./AdminUsers.css";

const STATUS = {
  ALL: "all",
  ACTIVE: "active",
  BLOCKED: "blocked",
  CANCELLED: "cancelled",
};

const STATUS_LABEL_KEYS = {
  [STATUS.ALL]: "adminUsersStatusAll",
  [STATUS.ACTIVE]: "adminUsersStatusActive",
  [STATUS.BLOCKED]: "adminUsersStatusBlocked",
  [STATUS.CANCELLED]: "adminUsersStatusCancelled",
};

const STATUS_TONES = {
  [STATUS.ACTIVE]: { tone: "good", icon: "pi pi-check-circle" },
  [STATUS.BLOCKED]: { tone: "bad", icon: "pi pi-lock" },
  [STATUS.CANCELLED]: { tone: "neutral", icon: "pi pi-minus-circle" },
};

const getStatus = (user) => {
  if (user.cancelled) return STATUS.CANCELLED;
  if (user.blocked) return STATUS.BLOCKED;
  return STATUS.ACTIVE;
};

// A data de cadastro é exibida em UTC (formatDate); o calendário devolve datas locais
const utcDayKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const localDayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const getInitials = (user) =>
  `${user.firstname?.charAt(0) || ""}${user.surname?.charAt(0) || ""}`.toUpperCase() ||
  user.username?.charAt(0)?.toUpperCase() ||
  "?";

const AdminUsers = ({ plan }) => {
  const { users, loading, loadError, updatingUsername, setBlocked, reload } = useAdminUsers(plan);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(STATUS.ALL);
  const [dateRange, setDateRange] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const isPromoter = plan === "salepromoter";

  const rows = useMemo(
    () =>
      users.map((user) => ({
        ...user,
        statusKey: getStatus(user),
        fullName: `${user.firstname || ""} ${user.surname || ""}`.trim(),
        createdDay: user.dateCreation ? utcDayKey(user.dateCreation) : null,
        documentsCount: getUserDocuments(user).length,
        searchText: buildSearchText([
          user.username,
          user.firstname,
          user.surname,
          user.email,
          user.phonenumber,
          user.referall,
        ]),
      })),
    [users],
  );

  const statusCounts = useMemo(
    () =>
      rows.reduce(
        (counts, row) => ({ ...counts, [row.statusKey]: counts[row.statusKey] + 1 }),
        { [STATUS.ALL]: rows.length, [STATUS.ACTIVE]: 0, [STATUS.BLOCKED]: 0, [STATUS.CANCELLED]: 0 },
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    const [start, end] = dateRange || [];
    // Uma data só = filtra aquele dia, como no filtro de data do admin clássico
    const startKey = start ? localDayKey(start) : null;
    const endKey = end ? localDayKey(end) : startKey;

    return rows.filter((row) => {
      if (status !== STATUS.ALL && row.statusKey !== status) return false;
      if (term && !row.searchText.includes(term)) return false;
      if (startKey && (!row.createdDay || row.createdDay < startKey || row.createdDay > endKey)) {
        return false;
      }
      return true;
    });
  }, [rows, search, status, dateRange]);

  const hasFilters = !!(search.trim() || status !== STATUS.ALL || dateRange?.[0]);

  const clearFilters = () => {
    setSearch("");
    setStatus(STATUS.ALL);
    setDateRange(null);
  };

  const confirmPendingAction = async () => {
    await setBlocked(pendingAction.username, pendingAction.block);
    setPendingAction(null);
  };

  const userTemplate = (user) => (
    <div className="admin-users__user">
      <span
        className={classNames("admin-users__avatar", {
          "admin-users__avatar--muted": user.cancelled,
        })}
        aria-hidden="true"
      >
        {user.cancelled ? <i className="pi pi-user-minus" /> : getInitials(user)}
      </span>
      <div className="admin-stack">
        <span className="admin-users__name">{user.fullName || user.username}</span>
        {user.fullName && <span className="admin-muted">@{user.username}</span>}
      </div>
    </div>
  );

  const contactTemplate = (user) =>
    user.email || user.phonenumber ? (
      <div className="admin-stack">
        {user.email && <span className="admin-users__email">{user.email}</span>}
        {user.phonenumber && <span className="admin-muted">{user.phonenumber}</span>}
      </div>
    ) : (
      <span className="admin-muted">—</span>
    );

  const statusTemplate = (user) => (
    <StatusBadge {...STATUS_TONES[user.statusKey]}>
      {localeText(STATUS_LABEL_KEYS[user.statusKey])}
    </StatusBadge>
  );

  const emptyMessage = loadError ? (
    <EmptyState
      icon="pi pi-exclamation-triangle"
      message={localeText("adminUsersLoadError")}
      actionLabel={localeText("adminUsersRetry")}
      onAction={reload}
    />
  ) : (
    <EmptyState
      icon="pi pi-users"
      message={localeText(hasFilters ? "adminUsersNoResults" : "adminUsersEmpty")}
      actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
      onAction={clearFilters}
    />
  );

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!pendingAction}
        message={
          pendingAction &&
          localeText(pendingAction.block ? "areYouSureBlockUser" : "areYouSureUnblockUser")
        }
        details={
          pendingAction
            ? [
                { label: localeText("adminUsersUser"), value: `@${pendingAction.username}` },
                { label: localeText("adminUsersContact"), value: pendingAction.email },
              ]
            : []
        }
        confirmLabel={localeText(pendingAction?.block ? "block" : "unblock")}
        confirmTone={pendingAction?.block ? "danger" : "primary"}
        loading={!!pendingAction && updatingUsername === pendingAction.username}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      <StatusTabs
        tabs={Object.values(STATUS).map((key) => ({
          value: key,
          label: localeText(STATUS_LABEL_KEYS[key]),
        }))}
        value={status}
        onChange={setStatus}
        counts={statusCounts}
        loading={loading}
        ariaLabel={localeText("adminUsersStatusFilter")}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("adminUsersSearchPlaceholder")}
          />
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            showIcon
            showButtonBar
            maxDate={new Date()}
            dateFormat="dd/mm/yy"
            placeholder={localeText("adminUsersCreatedBetween")}
            className="admin-list__calendar"
          />
          {hasFilters && (
            <button type="button" className="admin-link-btn" onClick={clearFilters}>
              <i className="pi pi-filter-slash" aria-hidden="true" />
              {localeText("adminUsersClearFilters")}
            </button>
          )}
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminUsersResults")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="dateCreation"
            sortOrder={-1}
            removableSort
            scrollable
            emptyMessage={emptyMessage}
            tableStyle={{ minWidth: isPromoter ? "68rem" : "62rem" }}
            rowClassName={(user) => ({ "admin-users__row--cancelled": user.cancelled })}
          >
            <Column
              header={localeText("adminUsersUser")}
              field="username"
              sortField="fullName"
              body={userTemplate}
              sortable
            />
            <Column header={localeText("adminUsersContact")} body={contactTemplate} />
            <Column
              header={localeText("referralUsername")}
              field="referall"
              body={(user) =>
                user.referall ? (
                  <span className="admin-muted">@{user.referall}</span>
                ) : (
                  <span className="admin-muted">—</span>
                )
              }
              sortable
            />
            <Column
              header={localeText("adminBackofficeDateCreation")}
              field="dateCreation"
              body={(user) => (user.dateCreation ? formatDate(user.dateCreation) : "—")}
              sortable
            />
            <Column header="Status" field="statusKey" body={statusTemplate} sortable />
            {isPromoter && (
              <Column
                header={localeText("documents")}
                field="documentsCount"
                body={(user) =>
                  user.documentsCount ? (
                    <span className="admin-users__docs">
                      <i className="pi pi-file" aria-hidden="true" />
                      {user.documentsCount}
                    </span>
                  ) : (
                    <span className="admin-muted">—</span>
                  )
                }
                sortable
              />
            )}
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(user) => (
                <UserActions
                  user={user}
                  showDocuments={isPromoter}
                  busy={updatingUsername === user.username}
                  onToggleBlock={(target) =>
                    setPendingAction({
                      username: target.username,
                      email: target.email,
                      block: !target.blocked,
                    })
                  }
                />
              )}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminUsers;
